import React from 'react';
import classnames from 'classnames';

type RadioSwitchOption = {
    name: React.ReactNode;
    value: string;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

export type RadioSwitchProps = Omit<
    React.ComponentPropsWithoutRef<'div'>,
    'defaultChecked' | 'checked'
> & {
    options: RadioSwitchOption[];
    handleOnChange: (value: string) => void;
    id: string;
    dynamic?: boolean;
} & (
        | {
              /**
               * Cannot be used together with `defaultChecked`
               */
              checked?: never;
              /**
               * Cannot be used together with `checked`
               */
              defaultChecked: string;
          }
        | {
              /**
               * Cannot be used together with `defaultChecked`
               */
              checked: string;
              /**
               * Cannot be used together with `checked`
               */
              defaultChecked?: never;
          }
    );

export const RadioSwitch = ({
    checked,
    defaultChecked,
    className,
    handleOnChange,
    id,
    options,
    dynamic = false,
    ...otherProps
}: RadioSwitchProps) => {
    return (
        <div
            className={classnames(
                'form-control-radio-switch',
                dynamic && 'd-inline-flex',
                className
            )}
            {...otherProps}>
            {options.map((option) => {
                return (
                    option.value && (
                        <span
                            className={classnames(
                                'form-check',
                                'form-check-inline',
                                option.className
                            )}
                            style={{
                                flexGrow: 1,
                                width: 'auto',
                                ...option.style
                            }}
                            key={option.value}>
                            <input
                                className="form-check-input"
                                type="radio"
                                name={id}
                                id={`${id}-${option.value}`}
                                value={option.value}
                                {...(typeof checked !== 'undefined' && {
                                    checked: option.value === checked
                                })}
                                {...(typeof defaultChecked !== 'undefined' && {
                                    defaultChecked:
                                        option.value === defaultChecked
                                })}
                                onChange={(e) => {
                                    if (!option.disabled)
                                        handleOnChange(e.target.value);
                                }}
                                disabled={option.disabled}
                            />
                            <label
                                className="form-check-label"
                                htmlFor={`${id}-${option.value}`}>
                                {option.name}
                            </label>
                        </span>
                    )
                );
            })}
        </div>
    );
};
